/** @jsxImportSource @emotion/react */
import { FocusPanelTitlebar } from '../FocusPanelTitlebar';
import { PROGRESS_ALT_PAGE, type ScanFocusPanelScopeProps } from '../scanFocusPanelTypes';

import { ProgressAltProvider, useProgressAlt } from './progressAltContext';
import { ProgressAltGrid } from './ProgressAltGrid';
import { ProgressAltPhotoView } from './ProgressAltPhotoView';
import { ProgressAltTileSizePicker } from './ProgressAltTileSizePicker';

/**
 * Which of the panel's two pages is showing, under the titlebar they share.
 *
 * The Progress Alt tab stays lit on both of them: the fullscreen page has no tab of its own, and it is
 * this same panel magnified rather than somewhere else — an unlit tab row would read as having left.
 *
 * No scope breadcrumb on either page. The trail named the scope the timeline was narrowed to, and
 * offered the way back out of that narrowing — neither of which exists now the grid always spans the
 * scan (KAIDEV-3881). What the reader is focused on is what they are scrolled to, which no trail can
 * state; the selected column's own lit header says which scope that is, as does the fullscreen page's
 * details heading.
 */
const ProgressAltBody = (props: ScanFocusPanelScopeProps) => {
  const { timeline, cursor } = useProgressAlt();

  return (
    <FocusPanelTitlebar
      {...props}
      activePanel={PROGRESS_ALT_PAGE}
      bodyVariant='flush'
      hideBreadcrumb
      trailing={cursor ? null : <ProgressAltTileSizePicker />}
    >
      {!timeline ? null : cursor ? <ProgressAltPhotoView /> : <ProgressAltGrid />}
    </FocusPanelTitlebar>
  );
};

/**
 * Progress Alt tab of the focused workspace: an experimental alternate take on the walk comparison
 * (KAIDEV-3865). Rows are time buckets, columns are every scoped group and scoped task of the scan,
 * and every cell shows the newest photo taken for that scope in that bucket.
 *
 * Two pages over the one timeline: the grid, and a full-bleed view of a single cell's photos
 * (KAIDEV-3873). Which one is showing is a matter of which route named the panel, and which cell
 * either of them is looking at is a matter of the group and task slots in that route (KAIDEV-3881) —
 * so a cell can be linked to, the back button leaves it, and the trip out to the editor and back is an
 * ordinary navigation. Selecting a scope scrolls the grid to its column rather than filtering the
 * timeline down to it — so what the reader is focused on is where the grid is scrolled to, not a subset
 * of the scan the panel has been narrowed to.
 *
 * Everything either page reads lives in `ProgressAltProvider` — one provider across both, so nothing
 * here has to carry the scan down to whatever draws it, and none of the panel's own state is lost on
 * the way in and out of fullscreen. See `progressAltContext`.
 */
export const FocusedProgressAltPanel = (props: ScanFocusPanelScopeProps) => (
  <ProgressAltProvider
    activePanel={props.activePanel}
    selectedGroupId={props.selectedGroupId}
    selectedTaskId={props.selectedTaskId}
    onSelectGroup={props.onSelectGroup}
    onSelectTask={props.onSelectTask}
  >
    <ProgressAltBody {...props} />
  </ProgressAltProvider>
);
